'use client';

import {
  Dialog,
  DialogContent,
  DialogOverlay,
  DialogTitle,
} from '@src/v3/components/coreUI/dialog';
import QRCodeGenerate from '@src/v3/components/coreUI/qrCodeGenerate';
import Icon from '@src/v3/components/coreUI/icon';
import Button from '@src/v3/components/coreUI/button';
import TextField from '@src/v3/components/coreUI/textField';
import { ERROR_MESSAGES } from '@src/v3/constants/messages';
import { showErrorMsg } from '@src/v3/utils/notifications';
import React, {
  useState,
  useMemo,
  useEffect,
  useRef,
  useCallback,
} from 'react';
import Image from 'next/image';
import { APP_ROUTE } from '@src/v3/constants/routes';
import { ProfileShareModalProps } from '@src/v3/interfaces/components/property/publicProfile';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../coreUI/tooltip';

// Constants
const COPIED_TIMEOUT = 3000;

const ProfileShareModal: React.FC<ProfileShareModalProps> = ({
  isOpen,
  onClose,
  profileId,
  profileImage,
  profileName,
  companyName,
}) => {
  /**
   * Generate the profile link based on profileId.
   * Using useMemo to recalculate when profileId changes.
   */
  const link = useMemo(() => {
    return profileId
      ? `${window?.location?.origin}${APP_ROUTE.AGENT_PROFILE.replace(
          '#USER_IDENTIFIER#',
          profileId
        )}`
      : '';
  }, [profileId]);

  const [isCopied, setIsCopied] = useState<boolean>(false);
  const [isLinkTruncated, setIsLinkTruncated] = useState<boolean>(false);
  const linkInputRef = useRef<HTMLDivElement>(null);

  // Check if link text is truncated
  const checkTruncation = useCallback(() => {
    if (linkInputRef.current) {
      const input = linkInputRef.current.querySelector('input');
      if (input) {
        setIsLinkTruncated(input.scrollWidth > input.clientWidth);
      }
    }
  }, []);

  // Check truncation on mount and resize
  useEffect(() => {
    if (!isOpen) return undefined;

    // Delay check to ensure DOM is rendered
    const timeoutId = setTimeout(checkTruncation, 100);

    window.addEventListener('resize', checkTruncation);
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', checkTruncation);
    };
  }, [isOpen, checkTruncation]);

  // Reset copied state after timeout
  useEffect(() => {
    if (isCopied) {
      const timer = setTimeout(() => {
        setIsCopied(false);
      }, COPIED_TIMEOUT);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [isCopied]);

  /**
   * Open native share dialog on mobile
   * Falls back to custom share modal if Web Share API is not supported
   */
  const handleNativeShare = async () => {
    const shareData = {
      title: profileName,
      url: link,
    };

    // Check if Web Share API is supported
    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch (error) {
        // User cancelled sharing or error occurred
        if ((error as Error).name !== 'AbortError') {
          console.error('Error sharing:', error);
          // Fallback to custom modal on error
        }
      }
    }
  };

  /**
   * Copy the link to the clipboard and show success message
   * Includes fallback for mobile devices
   */
  const copyLink = async () => {
    // Try modern Clipboard API first
    if (navigator.clipboard && navigator.clipboard.writeText) {
      try {
        await navigator.clipboard.writeText(link);
        setIsCopied(true);
        return;
      } catch (err) {
        console.error('Clipboard API failed, trying fallback:', err);
      }
    }

    // Fallback method for mobile devices
    try {
      // Create a temporary textarea element
      const textArea = document.createElement('textarea');
      textArea.value = link;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();

      // Use execCommand as fallback
      const successful = document.execCommand('copy');
      document.body.removeChild(textArea);

      if (successful) {
        setIsCopied(true);
      } else {
        throw new Error('execCommand copy failed');
      }
    } catch (err) {
      console.error('Failed to copy link:', err);
      // Show error message to user
      showErrorMsg(ERROR_MESSAGES.COPY_LINK_FAILED);
    }
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogOverlay />
      <DialogContent
        size="5xl"
        className="!p-0 !max-w-[1160px] h-full sm:max-h-[700px] sm:overflow-hidden"
        showCloseButton={false}
      >
        <div className="relative flex flex-col max-sm:h-full">
          {/* Header */}
          <div className="flex self-stretch px-8 py-6 bg-background-100 sm:border-b border-gray-300 justify-end sm:justify-between items-center">
            <DialogTitle className="!text-gray-1000 text-[24px] font-semibold hidden sm:block">
              Share this profile
            </DialogTitle>
            {/* Close Button */}
            <Button
              onClick={onClose}
              variant="ghost"
              size="icon"
              className="size-5 relative flex items-center justify-center focus-visible:outline-none cursor-pointer touch-manipulation hover:bg-transparent "
              aria-label="Close modal"
            >
              <Icon
                iconName="crossicon"
                iconProps={{
                  className: 'size-4 pointer-events-none',
                  style: { color: '#374151' },
                }}
              />
            </Button>
          </div>

          {/* Content - Two Column Layout */}
          <div className="grid grid-cols-1 sm:grid-cols-2 sm:items-stretch max-sm:px-6 max-sm:pb-6 flex-1 overflow-y-auto items-center">
            {/* Left Panel - QR Code Section */}
            <div
              className="flex flex-col justify-center items-center pt-8 max-sm:px-6 sm:p-8 gap-4 sm:gap-8 sm:border-r border-gray-200 xs:bg-background-100 w-full max-xs:bg-gradient-to-b max-xs:from-white max-xs:to-[#f0f0f0] max-xs:border max-xs:border-gray-200 max-xs:rounded-xl max-h-[650px] xs:h-[620px]"
              style={{
                paddingBottom: 'calc(2rem + env(safe-area-inset-bottom, 0px))',
              }}
            >
              <div className="sm:hidden flex flex-col gap-y-3 w-full max-w-full">
                <div className="w-[75px] h-[75px] rounded-full overflow-hidden bg-gray-200 flex-shrink-0 mx-auto">
                  {profileImage ? (
                    <Image
                      src={profileImage}
                      alt={profileName}
                      width={75}
                      height={75}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gray-300 text-gray-600 text-2xl font-semibold">
                      {profileName?.charAt(0)?.toUpperCase()}
                    </div>
                  )}
                </div>
                {/* Profile Name */}
                <div className="flex flex-col gap-y-1">
                  <span className="text-2xl font-medium text-gray-1000 capitalize text-center break-words w-full">
                    {profileName}
                  </span>
                  {/* Company Name */}
                  {companyName && (
                    <span className="text-sm font-medium text-gray-800 text-center break-words w-full">
                      {companyName}
                    </span>
                  )}
                </div>
              </div>
              {/* QR Code Section */}
              <div className="flex flex-col justify-center items-center gap-4 w-full">
                <h4 className="text-2xl font-semibold leading-normal tracking-[-0.24px] text-black">
                  Scan QR Code
                </h4>
                <div className="flex justify-center items-center flex-shrink-0 bg-blue-200 p-4 rounded-xl border border-gray-300">
                  {link && (
                    <div className="flex-shrink-0 bg-white rounded-md overflow-hidden">
                      <QRCodeGenerate value={link} size={182} />
                    </div>
                  )}
                </div>
              </div>

              {/* Link Section */}
              <div className="flex flex-col gap-6 relative w-full">
                {/* "Or share the link" with dividers */}
                <div className="flex justify-center items-center gap-6 self-stretch">
                  <div className="flex-1 h-px bg-gray-300" />
                  <p className="text-sm font-normal leading-6 tracking-[-0.14px] whitespace-nowrap text-black">
                    Or share the link
                  </p>
                  <div className="flex-1 h-px bg-gray-300" />
                </div>

                {/* Copy Link Section */}
                <div className="relative flex justify-center items-start gap-3 self-stretch">
                  {/* Copied Confirmation Pill */}
                  {isCopied && (
                    <div className="absolute -bottom-10 left-1/2 transform -translate-x-1/2 z-50">
                      <div
                        className="flex justify-center items-center gap-1 flex-shrink-0 rounded-lg h-[38px] px-3 py-3"
                        style={{
                          background: 'rgba(0, 0, 0, 0.44)',
                        }}
                      >
                        <span className="text-sm font-medium leading-normal text-white">
                          Copied
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Input Container */}
                  <div className="flex-1">
                    <TooltipProvider delayDuration={300}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div ref={linkInputRef}>
                            <TextField
                              value={link}
                              size="md"
                              variant="dark"
                              showLabel={false}
                              readOnly
                              inputClassName="sm:!text-lg !text-sm !font-normal cursor-pointer !border-gray-300 bg-white sm:truncate sm:text-ellipsis sm:overflow-hidden"
                              className="!h-[52px]"
                              onClick={copyLink}
                              startAdornment={
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    copyLink();
                                  }}
                                  className="flex-shrink-0 p-0 h-auto text-gray-1000 transition-opacity touch-manipulation"
                                  aria-label="Copy link"
                                >
                                  <Icon
                                    iconName="copyText"
                                    iconProps={{
                                      className: 'w-5 h-5',
                                    }}
                                  />
                                </Button>
                              }
                              onChange={() => {}}
                            />
                          </div>
                        </TooltipTrigger>
                        {isLinkTruncated && (
                          <TooltipContent
                            side="top"
                            align="center"
                            className="hidden sm:block bg-white text-gray-1000 shadow-lg max-w-[300px] break-all"
                          >
                            <p className="text-sm">{link}</p>
                          </TooltipContent>
                        )}
                      </Tooltip>
                    </TooltipProvider>
                  </div>

                  {/* Share Button - Mobile Only */}
                  <div className="sm:hidden">
                    <Button
                      variant="outline"
                      size="xl"
                      onClick={handleNativeShare}
                      className="!px-4 flex-shrink-0 shadow-none !border-gray-300"
                      aria-label="Share link"
                    >
                      <Icon
                        iconName="linkCopy"
                        iconProps={{
                          className: 'w-5 h-5',
                        }}
                      />
                    </Button>
                  </div>
                </div>
                <div className="sm:hidden block mx-auto mt-1">
                  <Image
                    src="/zeme-colored-logo.png"
                    alt="Zeme Logo"
                    width={100}
                    height={100}
                  />
                </div>
              </div>
            </div>

            {/* Right Panel - Profile Preview Card */}
            <div className="hidden sm:flex flex-col justify-center items-center p-8 gap-8 bg-neutral-50 ">
              <div
                className="flex flex-col items-start px-6 py-7 gap-1 rounded-xl overflow-hidden bg-gradient-to-b from-white to-[#f0f0f0] w-[353px] border border-gray-200"
                style={{
                  boxShadow:
                    '0px 64px 18px 0px rgba(0, 0, 0, 0.00), 0px 42px 16px 0px rgba(0, 0, 0, 0.00), 0px 23px 14px 0px rgba(0, 0, 0, 0.02), 0px 10px 10px 0px rgba(0, 0, 0, 0.03), 0px 3px 6px 0px rgba(0, 0, 0, 0.03)',
                }}
              >
                <div className="flex items-end justify-between w-full gap-2">
                  <div className="flex flex-col items-start">
                    {/* Profile Image */}
                    <div className="w-[95px] h-[95px] rounded-full overflow-hidden bg-gray-200 flex-shrink-0">
                      {profileImage ? (
                        <Image
                          src={profileImage}
                          alt={profileName}
                          width={95}
                          height={95}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-gray-300 text-gray-600 text-2xl font-semibold">
                          {profileName?.charAt(0)?.toUpperCase()}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Zeme Logo */}
                  <div className="flex-shrink-0 pb-1">
                    <Image
                      src="/zeme-colored-logo.png"
                      alt="Zeme Logo"
                      width={100}
                      height={100}
                    />
                  </div>
                </div>
                <div>
                  {/* Company Name */}
                  {companyName && (
                    <p className="text-sm font-medium leading-6 text-gray-800 mt-1.5">
                      {companyName}
                    </p>
                  )}
                  {/* Profile Name */}
                  <p className="text-2xl font-medium leading-9 text-gray-1000 capitalize sm:leading-7">
                    {profileName}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ProfileShareModal;
